import { useMemo, useState, type ReactNode } from 'react'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import type {
  IBuiltInInstrumentSummary,
  IInstrumentTemplateListResponse,
  IInstrumentTemplateSummary,
} from '@brushfire/types'
import { BuiltInInstrumentsSection } from '@/features/built-in-instruments/components'
import { useInstrumentTemplates } from '@/features/instruments/templates/api/template-queries'
import { InstrumentTemplateLibrarySkeleton } from './skeletons'
import { TemplateCategorySection } from './TemplateCategorySection'
import { TemplateLibrarySearch } from './TemplateLibrarySearch'
import { resolveInstrumentTemplateLibraryCardSx } from './instrumentTemplateLibraryCardSx'

const SECONDARY_TEXT = '#64748b'

function TemplateLibraryCardSubtitle({ cardSubtitle }: Readonly<{ cardSubtitle: ReactNode }>) {
  if (cardSubtitle === null) {
    return <Box sx={{ mb: 2 }} />
  }
  return (
    <Box sx={{ mb: 2 }}>
      {typeof cardSubtitle === 'string' ? (
        <Typography sx={{ fontSize: '14px', color: SECONDARY_TEXT }}>{cardSubtitle}</Typography>
      ) : (
        cardSubtitle
      )}
    </Box>
  )
}

function TemplateLibraryEmptyState({
  search,
  onClearSearch,
}: Readonly<{
  search: string
  onClearSearch: () => void
}>) {
  const trimmed = search.trim()
  return (
    <Box data-testid="instrument-template-library-empty" sx={{ textAlign: 'center', py: 5 }}>
      <Typography sx={{ fontSize: '14px', color: '#0f172a' }}>
        {trimmed.length > 0 ? (
          <>No templates found for &apos;{trimmed}&apos;. Try a different search. </>
        ) : (
          <>No templates found. Try a different search. </>
        )}
        <Link
          component="button"
          type="button"
          data-testid="instrument-template-library-clear-search"
          onClick={onClearSearch}
          sx={{ fontSize: '14px', verticalAlign: 'baseline' }}
        >
          clear search
        </Link>
      </Typography>
    </Box>
  )
}

type Props = Readonly<{
  /** When false, template cards are display-only (no preview / selection click). */
  interactive: boolean
  selectedTemplateId?: string | null
  onOpenTemplate?: (template: IInstrumentTemplateSummary) => void
  /** `create` — instrument picker card; `admin` — bordered panel; `bare` — nav Library, no outer card. */
  cardVariant?: 'create' | 'admin' | 'bare'
  /** Page title/subtitle live outside the card on admin variant. */
  showCardHeader?: boolean
  cardTitle?: string
  /** Plain text or skeleton nodes while parent data loads. */
  cardSubtitle?: ReactNode
  /** Controlled search — when set, inline search can be hidden via `showInlineSearch`. */
  searchValue?: string
  onSearchChange?: (value: string) => void
  /** Defaults to true; set false when search lives in AppShell header (Library page). */
  showInlineSearch?: boolean
  tileVariant?: 'sensemaker' | 'platformAdmin'
  onRequestHide?: (template: IInstrumentTemplateSummary) => void
  onRequestUnhide?: (template: IInstrumentTemplateSummary) => void
  /** When set, skips the default Sensemaker templates query (Platform Admin list). */
  listQuery?: {
    data?: IInstrumentTemplateListResponse
    isLoading: boolean
    isError: boolean
    refetch: () => void
  }
  /** BFP-606 — user-saved instruments below system categories. */
  builtInEntries?: IBuiltInInstrumentSummary[]
  canRemoveBuiltIn?: boolean
  onOpenBuiltIn?: (entry: IBuiltInInstrumentSummary) => void
  onRequestRemoveBuiltIn?: (entry: IBuiltInInstrumentSummary) => void
}>

export function InstrumentTemplateLibraryView({
  interactive,
  selectedTemplateId = null,
  onOpenTemplate,
  cardVariant = 'create',
  showCardHeader = true,
  cardTitle = 'Choose a template',
  cardSubtitle = null,
  searchValue,
  onSearchChange,
  showInlineSearch = true,
  tileVariant = 'sensemaker',
  onRequestHide,
  onRequestUnhide,
  listQuery,
  builtInEntries = [],
  canRemoveBuiltIn = false,
  onOpenBuiltIn,
  onRequestRemoveBuiltIn,
}: Props) {
  const [internalSearch, setInternalSearch] = useState('')
  const isControlledSearch = searchValue !== undefined && onSearchChange !== undefined
  const search = isControlledSearch ? searchValue : internalSearch
  const setSearch = isControlledSearch ? onSearchChange : setInternalSearch
  const sensemakerQuery = useInstrumentTemplates(search)
  const { data, isLoading, isError, refetch } = listQuery ?? {
    data: sensemakerQuery.data,
    isLoading: sensemakerQuery.isLoading,
    isError: sensemakerQuery.isError,
    refetch: () => {
      Promise.resolve(sensemakerQuery.refetch()).catch(() => undefined)
    },
  }

  const categories = data?.categories ?? []
  const totalTemplates = useMemo(
    () => categories.reduce((n, c) => n + c.templates.length, 0),
    [categories],
  )

  const cardSx = resolveInstrumentTemplateLibraryCardSx(cardVariant)

  return (
    <Box sx={cardSx} data-testid="instrument-template-library">
      {showCardHeader ? (
        <>
          <Typography sx={{ fontWeight: 700, fontSize: '20px', color: '#0f172a', mb: 0.5 }}>
            {cardTitle}
          </Typography>
          <TemplateLibraryCardSubtitle cardSubtitle={cardSubtitle} />
        </>
      ) : null}

      {showInlineSearch ? <TemplateLibrarySearch value={search} onChange={setSearch} /> : null}

      {isLoading ? <InstrumentTemplateLibrarySkeleton /> : null}

      {isError ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography sx={{ color: '#b91c1c', mb: 1 }}>Could not load templates.</Typography>
          <Link
            component="button"
            type="button"
            onClick={() => {
              Promise.resolve(refetch()).catch(() => undefined)
            }}
            sx={{ fontSize: '14px', cursor: 'pointer' }}
          >
            Try again
          </Link>
        </Box>
      ) : null}

      {!isLoading && !isError && totalTemplates === 0 && builtInEntries.length === 0 ? (
        <TemplateLibraryEmptyState search={search} onClearSearch={() => setSearch('')} />
      ) : null}

      {!isLoading && !isError
        ? categories.map((cat) => (
            <TemplateCategorySection
              key={cat.category}
              label={cat.label}
              templates={cat.templates}
              selectedTemplateId={selectedTemplateId}
              interactive={interactive}
              tileVariant={tileVariant}
              onRequestHide={onRequestHide}
              onRequestUnhide={onRequestUnhide}
              onOpenTemplate={onOpenTemplate}
            />
          ))
        : null}

      {onOpenBuiltIn && onRequestRemoveBuiltIn ? (
        <BuiltInInstrumentsSection
          entries={builtInEntries}
          canRemove={canRemoveBuiltIn}
          onOpenEntry={onOpenBuiltIn}
          onRequestRemove={onRequestRemoveBuiltIn}
        />
      ) : null}
    </Box>
  )
}
